/**
 * HistoryManager.ts;
 * Gestionnaire de l'historique des actions (undo/redo);
 * PHASE 1.3: Synchronise avec StateService
 */
import { logger } from './utils/ProfessionalLogger';
import { useAppStore } from './services/StateService';
/**
 * Type pour les données d'état de l'historique
 * Peut être n'importe quelle structure de données sérialisable
 */
export type HistoryStateData =
  | Record<string, unknown>
  | unknown[]
  | string
  | number
  | boolean
  | null;

export interface HistoryState {
  id: string;
  timestamp: number;
  data: HistoryStateData;
  description?: string;
}
export class HistoryManager {
  private history: HistoryState[] = [];
  private currentIndex: number = -1;
  private maxStates: number;
  private isEnabled: boolean = true;
  // CORRIGÉ: Limite de mémoire pour éviter les fuites (environ 10MB par défaut)
  private maxMemoryBytes: number = 10 * 1024 * 1024; // 10MB
  private currentMemoryUsage: number = 0;

  constructor(maxStates: number = 50, maxMemoryBytes: number = 10 * 1024 * 1024) {
    this.maxStates = maxStates;
    this.maxMemoryBytes = maxMemoryBytes;
  }
  /**
   * Sauvegarde un état dans l'historique;
   * PHASE 1.3: Synchronise avec StateService
   * CORRIGÉ: Gestion de la mémoire pour éviter les fuites
   */
  public saveState(data: HistoryStateData, description?: string): void {
    if (!this.isEnabled) return;

    // Supprimer les états futurs si on n'est pas à la fin;
    if (this.currentIndex < this.history.length - 1) {
      // Libérer la mémoire des états futurs supprimés
      const removedStates = this.history.slice(this.currentIndex + 1);
      removedStates.forEach(state => {
        this.currentMemoryUsage -= this.estimateStateSize(state);
      });
      this.history = this.history.slice(0, this.currentIndex + 1);
    }

    // Créer le nouvel état;
    const newState: HistoryState = {
      id: this.generateId(),
      timestamp: Date.now(),
      data: structuredClone(data), // Deep clone optimisé;
      description: description || 'Action',
    };

    // Estimer la taille du nouvel état
    const newStateSize = this.estimateStateSize(newState);

    // Vérifier si on dépasse la limite de mémoire
    while (
      this.currentMemoryUsage + newStateSize > this.maxMemoryBytes &&
      this.history.length > 0
    ) {
      // Supprimer les états les plus anciens
      const removedState = this.history.shift();
      if (removedState) {
        this.currentMemoryUsage -= this.estimateStateSize(removedState);
        this.currentIndex--;
      }
    }

    // Ajouter l'état;
    this.history.push(newState);
    this.currentMemoryUsage += newStateSize;

    // Limiter le nombre d'états (fallback si la limite mémoire n'est pas atteinte)
    if (this.history.length > this.maxStates) {
      const removedState = this.history.shift();
      if (removedState) {
        this.currentMemoryUsage -= this.estimateStateSize(removedState);
      }
      this.currentIndex = this.history.length - 1;
    } else {
      this.currentIndex++;
    }

    // PHASE 1.3: Synchroniser avec StateService
    this.syncWithStateService();

    logger.info(
      `📝 État sauvegardé: ${newState.description} (Mémoire: ${Math.round(this.currentMemoryUsage / 1024)}KB)`
    );
  }

  /**
   * Estime la taille en mémoire d'un état
   * CORRIGÉ: Pour la gestion de la mémoire
   */
  private estimateStateSize(state: HistoryState): number {
    try {
      return JSON.stringify(state).length * 2; // Approximation: 2 bytes par caractère UTF-16
    } catch (_e) {
      // Si la sérialisation échoue, estimation conservatrice
      return 1024; // 1KB par défaut
    }
  }
  /**
   * Annule la dernière action;
   * PHASE 1.3: Synchronise avec StateService
   */
  public undo(): HistoryStateData | null {
    if (!this.canUndo()) return null;
    this.currentIndex--;
    const state = this.history[this.currentIndex];

    // PHASE 1.3: Synchroniser avec StateService
    this.syncWithStateService();

    logger.info(`↶ Annulation: ${state.description}`);
    return state.data;
  }
  /**
   * Refait la dernière action annulée;
   * PHASE 1.3: Synchronise avec StateService
   */
  public redo(): HistoryStateData | null {
    if (!this.canRedo()) return null;
    this.currentIndex++;
    const state = this.history[this.currentIndex];

    // PHASE 1.3: Synchroniser avec StateService
    this.syncWithStateService();

    logger.info(`↷ Refait: ${state.description}`);
    return state.data;
  }
  /**
   * Vérifie si on peut annuler;
   */
  public canUndo(): boolean {
    return this.currentIndex > 0;
  }
  /**
   * Vérifie si on peut refaire;
   */
  public canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }
  /**
   * Obtient la longueur de l'historique;
   */
  public getHistoryLength(): number {
    return this.history.length;
  }
  /**
   * Obtient l'index actuel;
   */
  public getCurrentIndex(): number {
    return this.currentIndex;
  }
  /**
   * Obtient l'état actuel;
   */
  public getCurrentState(): HistoryState | null {
    if (this.currentIndex >= 0 && this.currentIndex < this.history.length) {
      return this.history[this.currentIndex];
    }
    return null;
  }
  /**
   * Obtient un état à un index spécifique;
   */
  public getStateAt(index: number): HistoryState | null {
    if (index >= 0 && index < this.history.length) {
      return this.history[index];
    }
    return null;
  }
  /**
   * Obtient tous les états;
   */
  public getAllStates(): HistoryState[] {
    return [...this.history];
  }
  /**
   * Obtient les états récents;
   */
  public getRecentStates(count: number = 10): HistoryState[] {
    return this.history.slice(-count);
  }
  /**
   * Vide l'historique;
   * CORRIGÉ: Réinitialise aussi l'usage mémoire
   */
  public clear(): void {
    this.history = [];
    this.currentIndex = -1;
    this.currentMemoryUsage = 0;
    logger.info('🧹 Historique vidé');
  }
  /**
   * Active/désactive l'historique;
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }
  /**
   * Vérifie si l'historique est activé
   */
  public isHistoryEnabled(): boolean {
    return this.isEnabled;
  }
  /**
   * Définit le nombre maximum d'états;
   */
  public setMaxStates(maxStates: number): void {
    this.maxStates = maxStates;
    // Tronquer l'historique si nécessaire;
    if (this.history.length > maxStates) {
      this.history = this.history.slice(-maxStates);
      this.currentIndex = this.history.length - 1;
    }
  }
  /**
   * Obtient le nombre maximum d'états;
   */
  public getMaxStates(): number {
    return this.maxStates;
  }
  /**
   * Synchronise l'état de l'historique avec StateService
   * PHASE 1.3: Met à jour canUndo, canRedo, historyLength dans le Store
   */
  private syncWithStateService(): void {
    const store = useAppStore.getState();
    store.setHistoryState(this.canUndo(), this.canRedo(), this.history.length);
  }

  /**
   * Génère un ID unique sécurisé;
   */
  private generateId(): string {
    // Utiliser crypto si disponible pour une meilleure unicité
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `state_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
    }
    // Fallback avec crypto.getRandomValues
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return `state_${Date.now()}_${array[0].toString(36)}`;
    }
    // Fallback final
    return `state_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  /**
   * Obtient des statistiques sur l'historique;
   * CORRIGÉ: Utilise l'estimation de mémoire optimisée
   */
  public getStatistics(): {
    totalStates: number;
    currentIndex: number;
    canUndo: boolean;
    canRedo: boolean;
    memoryUsage: number;
    memoryUsageMB: number;
    maxMemoryMB: number;
    memoryUsagePercent: number;
  } {
    return {
      totalStates: this.history.length,
      currentIndex: this.currentIndex,
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      memoryUsage: this.currentMemoryUsage,
      memoryUsageMB: Math.round((this.currentMemoryUsage / (1024 * 1024)) * 100) / 100,
      maxMemoryMB: Math.round((this.maxMemoryBytes / (1024 * 1024)) * 100) / 100,
      memoryUsagePercent: Math.round((this.currentMemoryUsage / this.maxMemoryBytes) * 100),
    };
  }

  /**
   * Définit la limite de mémoire pour l'historique
   * CORRIGÉ: Permet d'ajuster la limite selon les besoins
   */
  public setMaxMemory(maxMemoryBytes: number): void {
    this.maxMemoryBytes = maxMemoryBytes;
    // Nettoyer si nécessaire
    while (this.currentMemoryUsage > this.maxMemoryBytes && this.history.length > 0) {
      const removedState = this.history.shift();
      if (removedState) {
        this.currentMemoryUsage -= this.estimateStateSize(removedState);
        this.currentIndex--;
      }
    }
    logger.info(`💾 Limite mémoire historique: ${Math.round(maxMemoryBytes / (1024 * 1024))}MB`);
  }
}
